import { Alert, AlertTitle, Box, Grid, Link, Stack, Typography } from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { GetWasteContainerQuery, PackingGroup, UnitType } from "generated-graphql/graphql";
import { Link as RouterLink } from "react-router-dom";
import { buildWasteStreamUrl } from "util/wasteUrls";

import { prettyPrintPhysicalState } from "../Facilities/Facility/WasteStreamDetails/Profile/profileDisplay";
import { SHIPMENT_STATUS_LABELS } from "../Shipments/shipmentDisplay";
import { WasteClassificationChip } from "../WasteClassificationChip";
import {
  containerTypeLabel,
  formatContainerDate,
  formatQuantity,
  formatReportablePounds,
  MUST_TRANSPORT_200_MILES_SHORT_LABEL,
  NO_VALUE_PLACEHOLDER,
  reportablePounds,
} from "./containerDisplay";
import { FieldValue, SectionCard } from "./containerSections";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type ContainerShipmentRef = NonNullable<WasteContainerDetail["shippedOnShipment"]>;

function yesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

/** "PG II" — omitted entirely for waste that carries no packing group. */
function packingGroupLabel(group: PackingGroup | null | undefined): string | null {
  if (!group || group === PackingGroup.None) {
    return null;
  }
  return `PG ${group}`;
}

/**
 * The shipping description as it will appear on the manifest, e.g.
 * "Waste flammable liquid, n.o.s., Class 3 PG II, UN1219".
 *
 * Each piece is optional and a profile can be characterized far enough to have
 * some but not all of them, so the line is joined from whatever is known
 * rather than templated — a half-filled template would print stray commas and
 * a bare "Class".
 */
function dotShippingDescription(label: WasteContainerDetail["label"]): string {
  const parts = [
    label.dotProperShippingName,
    label.dotHazardClass ? `Class ${label.dotHazardClass}` : null,
    packingGroupLabel(label.dotPackingGroup),
    label.dotIdNumber,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : NO_VALUE_PLACEHOLDER;
}

/**
 * The shipment that carried this container.
 *
 * `tenantId` can be absent while the tenant is still resolving, and a link
 * built without it points at `/o/undefined/...`. The number is still worth
 * showing then, just not as something to click.
 */
export function ShipmentLink({
  shipment,
  tenantId,
}: {
  shipment: ContainerShipmentRef | null | undefined;
  tenantId: string | undefined;
}) {
  if (!shipment) {
    return NO_VALUE_PLACEHOLDER;
  }
  if (!tenantId) {
    return shipment.manifestRequestNumber;
  }
  return (
    <Link component={RouterLink} to={`/o/${tenantId}/waste/shipments/${shipment.id}`}>
      {shipment.manifestRequestNumber}
    </Link>
  );
}

/**
 * The official manifest for that shipment.
 *
 * The tracking number comes from the shipment's own snapshot, so it survives
 * the window where the pipeline has deleted the manifest row and not yet
 * rebuilt it. Without an id there is nothing to open, so it renders as text —
 * the same rule the shipment page's rail follows.
 */
function ManifestLink({
  shipment,
  tenantId,
}: {
  shipment: ContainerShipmentRef | null | undefined;
  tenantId: string | undefined;
}) {
  if (!shipment?.manifestTrackingNumber) {
    return NO_VALUE_PLACEHOLDER;
  }
  if (!shipment.processedManifestId || !tenantId) {
    return shipment.manifestTrackingNumber;
  }
  return (
    <Link
      component={RouterLink}
      to={`/o/${tenantId}/waste/manifests/${shipment.processedManifestId}`}
    >
      {shipment.manifestTrackingNumber}
    </Link>
  );
}

/**
 * What the waste is: the stream it belongs to and the characterization the
 * shipping paperwork will be built from.
 */
export function ContainerWasteCard({
  container,
  tenantId,
  showStreamLink,
}: {
  container: WasteContainerDetail;
  tenantId: string | undefined;
  showStreamLink: boolean;
}) {
  const profile = container.wasteStream?.profile;
  const streamHref =
    showStreamLink && tenantId && container.wasteStream
      ? buildWasteStreamUrl(tenantId, container.wasteStream.id)
      : null;
  return (
    <SectionCard title="Waste &amp; classification information">
      <Grid container spacing={2}>
        {/*
         * The stream link goes to its detail drawer, which is where the waste
         * profile lives. It matters here because the streams grid filters on
         * shipment lines, so it does not list a stream that has never shipped.
         * For a stream that only exists for container tracking, this is the
         * only route to its profile.
         *
         * Only a link when manifest-views-v2 is on. The streams route is gated
         * on that flag with `fallback="../"`, and this drawer is gated on
         * container-tracking, so a tenant with one flag and not the other would
         * click through and land on the waste overview.
         */}
        <FieldValue label="Waste stream">
          {container.wasteStream && streamHref && (
            <Link component={RouterLink} to={streamHref}>
              {container.wasteStream.name}
            </Link>
          )}
          {container.wasteStream && !streamHref && container.wasteStream.name}
          {!container.wasteStream && NO_VALUE_PLACEHOLDER}
        </FieldValue>
        <FieldValue label="Classification">
          {container.wasteStream?.classification ? (
            <WasteClassificationChip
              classification={container.wasteStream.classification}
              variant="soft"
            />
          ) : (
            NO_VALUE_PLACEHOLDER
          )}
        </FieldValue>
        <FieldValue label="Physical state">
          {prettyPrintPhysicalState(profile?.physicalState) ?? NO_VALUE_PLACEHOLDER}
        </FieldValue>
        {/* The profile number is the vendor's handle for this waste; it is
            useless without knowing whose it is, so the TSDF rides along with
            it the way the specs write it ("PRF 2291, Clean Earth"). */}
        <FieldValue label="Profile #">
          {profile?.profileNumber
            ? [profile.profileNumber, profile.tsdfName].filter(Boolean).join(", ")
            : NO_VALUE_PLACEHOLDER}
        </FieldValue>
        {/* This drawer is only reachable for container-tracking tenants (the
            wasteContainer query is ContainerTracking-gated), so the
            reportability field renders without an extra flag check. */}
        <FieldValue label="Counts toward generation">
          {yesNo(container.countsTowardGeneration)}
        </FieldValue>
      </Grid>
    </SectionCard>
  );
}

/**
 * How this container leaves the site: the shipping description that will go on
 * the manifest, and where the paperwork currently stands.
 */
export function ContainerShippingCard({
  container,
  tenantId,
}: {
  container: WasteContainerDetail;
  tenantId: string | undefined;
}) {
  // Requested-but-unreturned is the state worth calling out: the vendor has the
  // request and the official manifest is what everyone is waiting on. A draft
  // shipment is still ours to edit, so it isn't waiting on anybody.
  const awaitingManifest =
    container.shippedOnShipment == null &&
    container.openShipments.some((shipment) => shipment.status === "REQUESTED");
  // The codes the manifest and the printed label will carry, federal first.
  const wasteCodes = [...container.label.federalWasteCodes, ...container.label.stateWasteCodes];

  return (
    <SectionCard title="Shipping &amp; manifest">
      <Grid container spacing={2}>
        <FieldValue label="DOT name">{dotShippingDescription(container.label)}</FieldValue>
        <FieldValue label="Waste codes">
          {wasteCodes.length > 0 ? wasteCodes.join(", ") : NO_VALUE_PLACEHOLDER}
        </FieldValue>
        {/* A container awaiting pickup is on a shipment that has not left yet,
            and the estimated pickup date it shows comes from that shipment —
            so there has to be a way back to it. */}
        <FieldValue label="On shipment">
          {container.openShipments.length > 0
            ? container.openShipments.map((shipment, index) => (
                <Box component="span" key={shipment.id}>
                  {index > 0 && ", "}
                  <ShipmentLink shipment={shipment} tenantId={tenantId} />
                  <Typography component="span" variant="body2" color="text.secondary">
                    {` (${SHIPMENT_STATUS_LABELS[shipment.status]})`}
                  </Typography>
                </Box>
              ))
            : NO_VALUE_PLACEHOLDER}
        </FieldValue>
        <FieldValue label="Shipped on">
          <ShipmentLink shipment={container.shippedOnShipment} tenantId={tenantId} />
        </FieldValue>
        <FieldValue label="Manifest">
          <ManifestLink shipment={container.shippedOnShipment} tenantId={tenantId} />
        </FieldValue>
        <FieldValue label="Shipped date">{formatContainerDate(container.shippedDate)}</FieldValue>
        <FieldValue label="Estimated pickup date">
          {formatContainerDate(container.estimatedPickupDate)}
        </FieldValue>
      </Grid>
      {awaitingManifest && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>Awaiting vendor manifest</AlertTitle>
          Packaged into a manifest request and sent to the vendor. We&apos;ll reconcile the official
          manifest against this request when it&apos;s returned.
        </Alert>
      )}
    </SectionCard>
  );
}

/**
 * "≈ 1,500 lb" under an amount that converts to weight; a muted "specific
 * gravity needed" hint under a volume whose stream has no specific gravity.
 * Renders nothing when there's no amount to annotate.
 */
function ReportableAmountHint({
  amount,
  unit,
  specificGravity,
}: {
  amount: number | null | undefined;
  unit: UnitType | null | undefined;
  specificGravity: number | null | undefined;
}) {
  if (amount == null || !unit) {
    return null;
  }
  const lb = reportablePounds(amount, unit, specificGravity);
  if (lb !== null) {
    return (
      <Typography variant="caption" color="text.secondary" display="block">
        {formatReportablePounds(lb)}
      </Typography>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={0.25}>
      <Typography variant="caption" color="text.secondary" fontStyle="italic">
        Specific gravity needed to compute pounds
      </Typography>
      <InfoIconTooltip tooltip="This amount is recorded by volume. Once the waste stream has a specific gravity, this container's amount will also show in pounds." />
    </Stack>
  );
}

/**
 * The container's own record: the vessel, the dates, and the flags that change
 * which rules apply to it.
 *
 * Not in the specs' drawer, which folds the vessel into the header line — but
 * these are all values the edit form can set, and a field the reader can
 * change while never being shown its current value is a trap. What the header
 * already says (facility, type, capacity, area) is not repeated here.
 */
export function ContainerDetailsCard({
  container,
  tenantId,
}: {
  container: WasteContainerDetail;
  tenantId: string | undefined;
}) {
  return (
    <SectionCard title="Details">
      <Grid container spacing={2}>
        <FieldValue label="Facility">
          {container.facility?.name && tenantId ? (
            <Link
              component={RouterLink}
              to={`/o/${tenantId}/waste/facilities/${container.facilityId}`}
            >
              {container.facility.name}
            </Link>
          ) : (
            (container.facility?.name ?? NO_VALUE_PLACEHOLDER)
          )}
        </FieldValue>
        <FieldValue label="Container type">
          {containerTypeLabel(container.containerType)}
        </FieldValue>
        <FieldValue label="Capacity">
          {formatQuantity(container.capacity, container.capacityUnit)}
        </FieldValue>
        <FieldValue
          label="Amount on hand"
          secondary={
            <ReportableAmountHint
              amount={container.amount}
              unit={container.amountUnit}
              specificGravity={container.wasteStream?.profile?.specificGravity}
            />
          }
        >
          {formatQuantity(container.amount, container.amountUnit)}
          {container.isEstimate && container.amount != null ? " (estimate)" : ""}
        </FieldValue>
        <FieldValue label="Accumulation start date">
          {formatContainerDate(container.accumulationStartDate)}
        </FieldValue>
        <FieldValue label="Moved to central date">
          {formatContainerDate(container.movedToCentralDate)}
        </FieldValue>
        <FieldValue label="Permanent vessel (tank)">{yesNo(container.isPermanent)}</FieldValue>
        <FieldValue label="Flow-through">{yesNo(container.isFlowThrough)}</FieldValue>
        <FieldValue label="Amounts are estimates">{yesNo(container.isEstimate)}</FieldValue>
        <FieldValue label={MUST_TRANSPORT_200_MILES_SHORT_LABEL}>
          {yesNo(container.sqgOver200Miles)}
        </FieldValue>
      </Grid>
    </SectionCard>
  );
}
